/* eslint-disable @angular-eslint/no-output-on-prefix */
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { noop } from 'lodash';
import { AutoFocusDirective, ButtonComponent, InputComponent, LoaderComponent, MbsPopupType } from 'mbs-ui-kit';
import { TfaConfigureAlertComponent } from 'mbs-ui-shared@components/tfa-configure-alert/tfa-configure-alert.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { catchError, of, take } from 'rxjs';

@Component({
  selector: 'mbs-google-qr-code',
  templateUrl: './google-qr-code.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AutoFocusDirective,
    CanPipe,
    CommonModule,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    LoaderComponent,
    TfaConfigureAlertComponent,
    I18NextPipe,
    InputComponent
  ]
})
export class GoogleQRCodeComponent implements OnInit {
  public readonly permissionEnum = PermissionsEnum;
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private TFAService = inject(TFAService);
  authFacade = inject(AuthFacade);
  private loginFacade = inject(LoginFacade);
  private appBaseHref = inject(APP_BASE_HREF);

  public setupKey: string = null;
  public QRCodeUri: string = null;
  public authId: string = null;
  public isQRCodeLoading = true;
  public code = null;
  public isInvalidCode = false;
  public isLoading = false;
  public readonly alertType = MbsPopupType;
  public isQRCodeInvalid = false;
  public error = null;
  public skipLoading = false;

  @Output() onToggleAuthType = new EventEmitter();
  @Output() onQRCodeSet = new EventEmitter();

  @Input() isFirstTimeLogin = false;
  @Input() isLogin = false;

  ngOnInit(): void {
    this.setQRCode();
  }

  toggleAuthType(): void {
    this.onToggleAuthType.emit();
  }

  setQRCode(): void {
    this.isQRCodeInvalid = false;
    this.isQRCodeLoading = true;
    this.QRCodeUri = null;
    this.authId = null;
    this.error = null;

    this.TFAService.getGoogleQRCode()
      .pipe(
        take(1),
        catchError((error) => {
          this.isQRCodeInvalid = true;
          this.error = error.error.detail;
          return of(error);
        })
      )
      .subscribe((result) => {
        this.isQRCodeLoading = false;
        this.QRCodeUri = result.payload;
        this.setupKey = result.encodedSecretCode;
        this.authId = result.authId;
        this.onQRCodeSet.emit(this.authId);
        this.cdr.detectChanges();
      });
  }

  validate(): void {
    this.isLoading = true;
    this.isInvalidCode = false;
    this.TFAService.activateGoogle(this.code, this.authId)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe((res) => {
        if (!res?.isValid) {
          this.isLoading = false;
          this.isInvalidCode = true;
        }
        this.cdr.detectChanges();
      });
  }

  toLoginPage(): void {
    this.loginFacade.logout();
  }

  skip(): void {
    this.skipLoading = true;
    this.TFAService.refuse()
      .pipe(take(1))
      .subscribe(
        () => {
          this.router.navigateByUrl(RoutingPath.ApDashboard).catch(noop);
        },
        () => {
          this.skipLoading = false;
        }
      );
  }
}
